import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { SnackbarKey, closeSnackbar } from 'notistack'
import { Router } from '@remix-run/router'
import { useMountEffect } from '../../hooks/useMountEffect'

const defaultNotistackSnackbarKeyContextValue = {
  /** enqueueSnackbar from notistack returns a SnackbarKey, which we track so our provider closes it when the history listener is triggered */
  snackbarKeys: [] as SnackbarKey[],
  /** Provide a single SnackbarKey, or call enqueueSnackbar directly to add the key to the list to close */
  addSnackbarKey: (keyToAdd: SnackbarKey) => {
    console.warn(
      `The default value of NotistackSnackbarContext.addSnackbarKey was called with value: ${keyToAdd}. Did you forget to install a NotistackSnackbarProvider?`
    )
  },
}

export const NotistackSnackbarKeyContext = React.createContext(
  defaultNotistackSnackbarKeyContextValue
)
export const useNotistackSnackbarKeyContext =
  (): typeof defaultNotistackSnackbarKeyContextValue =>
    React.useContext(NotistackSnackbarKeyContext)

export type TestNotistackSnackbarKeyContextConfig = Partial<
  typeof defaultNotistackSnackbarKeyContextValue
>
interface NotistackSnackbarKeyContextProps extends PropsWithChildren {
  /** Used to subscribe to the navigation and handleClose each time we do navigate */
  router?: Router
  testConfig?: TestNotistackSnackbarKeyContextConfig
}

export const NotistackSnackbarKeyProvider: React.FC<
  NotistackSnackbarKeyContextProps
> = ({ router, children }) => {
  const [snackbarKeys, setSnackbarKeys] = useState<SnackbarKey[]>([])

  /**
   * Used in tandem with the return value of enqueueSnackbar from notistack.
   * @param keyToAdd SnackbarKey returned when adding a snackbar from enqueueSnackbar
   */
  const addSnackbarKey = (keyToAdd: SnackbarKey) => {
    setSnackbarKeys((currKeys) => [...currKeys, keyToAdd])
  }

  /** When snackbarKeys updates, update the subscriber and close the snackbars accordingly */
  const subscriber = useCallback(() => {
    router?.subscribe(() => {
      // Go through all keys we're tracking and close them
      for (const key of snackbarKeys) {
        closeSnackbar(key)
      }
      setSnackbarKeys([])
    })
  }, [router, snackbarKeys])

  useEffect(() => {
    /** Whenever we have keys */
    if (snackbarKeys.length > 0) {
      subscriber()
    }
  })

  /** When the location changes, do something https://github.com/remix-run/history/blob/dev/docs/api-reference.md#history.listen */
  useMountEffect(() => {
    router?.subscribe(() => {
      for (const key of snackbarKeys) {
        closeSnackbar(key)
      }
      if (snackbarKeys.length > 0) setSnackbarKeys([])
    })
  })

  return (
    <NotistackSnackbarKeyContext.Provider
      value={{
        snackbarKeys,
        addSnackbarKey,
      }}
    >
      {children}
    </NotistackSnackbarKeyContext.Provider>
  )
}

export default NotistackSnackbarKeyProvider
